import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Camera as CameraIcon, RefreshCcw, Sparkles, X, Image as ImageIcon,
  Crop as CropIcon, Bookmark
} from "lucide-react";
import { plants } from "@/lib/api";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import PhotoEditor from "@/components/PhotoEditor";
import BulkQueueView from "@/components/camera/BulkQueueView";
import MultiAngleControls from "@/components/camera/MultiAngleControls";
import { compressImage, readFileAsDataURL } from "@/lib/imageCompress";
import { MAX_BULK, isSupportedImage, getLocationOnce } from "@/lib/cameraUtils";
import { safeExifDateTaken } from "@/lib/exif";
import { useIdentifyRetryQueue } from "@/hooks/useIdentifyRetryQueue";
import { useBulkIdentify } from "@/hooks/useBulkIdentify";
import { p50IdentifyMs, formatEtaMs, recordIdentifyDurations, identifySampleCount } from "@/lib/identifyEta";

// Byte-budget compression profiles. `thumb` (~40KB) for queue previews,
// `quickRetry` (~500KB) for camera-retry drafts, `photo` (~900KB) for
// everything else. The budget keeps plant_scans docs safely under Mongo's
// 16MB BSON limit even after 5 extras stack.
const compressDataURL = (dataUrl, maxW = 1280) => {
  if (maxW <= 300) return compressImage(dataUrl, { profile: "thumb" });
  if (maxW <= 1024) return compressImage(dataUrl, { profile: "quickRetry" });
  return compressImage(dataUrl, { profile: "photo" });
};

export default function Camera() {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyStartedAt, setBusyStartedAt] = useState(null);
  const [flash, setFlash] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [includeLocation, setIncludeLocation] = useState(false);
  const [multiAngle, setMultiAngle] = useState(false);
  const [multiShots, setMultiShots] = useState([]);
  const navigate = useNavigate();

  const bulk = useBulkIdentify({ includeLocation });
  const { bulkQueue, bulkRunning } = bulk.state;

  // Force a re-render every 1 second while a bulk batch is running so the
  // elapsed / ETA readout stays live. Cleared as soon as the batch ends.
  const [, setEtaTick] = useState(0);
  useEffect(() => {
    if (!bulkRunning && !busy) return undefined;
    const id = setInterval(() => setEtaTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [bulkRunning, busy]);

  // Elapsed seconds since a single-photo / multi-angle identify started —
  // rendered as "Identifying… 12s" on the button once it crosses 3s so
  // the user knows something is happening during the slow Gemini call.
  const singleElapsedS = busy && busyStartedAt
    ? Math.floor((Date.now() - busyStartedAt) / 1000)
    : 0;
  const singleBusyLabel = busy && singleElapsedS >= 3
    ? t("camera.identifyingWithElapsed", { time: formatEtaMs(singleElapsedS * 1000) })
    : t("camera.identifying");

  // Rolling-p50 latency hint. We only show it once the user has ≥ 3
  // measured identify runs in this browser — before that, the fallback
  // estimate would be a bald default (20s) that isn't personal enough
  // to be helpful. Rounded to the nearest second so the copy reads clean.
  const latencySampleCount = identifySampleCount();
  const latencyHintSeconds = latencySampleCount >= 3 ? Math.round(p50IdentifyMs() / 1000) : null;

  useEffect(() => {
    if (snapshot || bulkQueue.length) return;
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: facing } }, audio: false,
        });
        if (!active) { stream.getTracks().forEach((tr) => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setError(null);
      } catch (e) {
        if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
          setError(t("camera.noCamera"));
        } else {
          setError(t("camera.blocked"));
        }
      }
    })();
    return () => {
      active = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
    };
  }, [facing, snapshot, bulkQueue.length, t]);

  const capture = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    const url = c.toDataURL("image/jpeg", 0.92);
    setFlash(true); setTimeout(() => setFlash(false), 350);
    if (multiAngle) {
      if (multiShots.length >= 4) { toast.error(t("camera.multiMaxed")); return; }
      const compact = await compressDataURL(url, 1280);
      setMultiShots((arr) => [...arr, compact]);
    } else {
      setSnapshot(url);
    }
  };

  const retake = () => setSnapshot(null);

  // Retry-queue helpers used only by the single-shot + multi-angle flows.
  // Bulk uses its own copy via `useBulkIdentify`.
  const { isTransientServerFailure, identifyWithOneRetry, queueTransientFailure } = useIdentifyRetryQueue();

  const offerSaveForLaterOnError = async (err, fallbackKey, compressedImage, loc) => {
    if (isTransientServerFailure(err)) {
      const queued = await queueTransientFailure(compressedImage, loc, {
        onQueued: () => setSnapshot(null),
      });
      if (queued) return;
      toast.error(t("camera.toastIdentifySlow"), {
        description: t("camera.toastIdentifySlowDesc"),
        duration: 10000,
        action: {
          label: t("camera.saveForLater"),
          onClick: () => saveForLater(),
        },
      });
    } else {
      const msg = err?.response?.data?.detail || t(fallbackKey);
      toast.error(msg);
    }
  };

  const saveForLater = async () => {
    if (!snapshot) return;
    setBusy(true);
    try {
      const compact = await compressDataURL(snapshot, 1024);
      const loc = includeLocation ? await getLocationOnce() : null;
      const dateTaken = safeExifDateTaken(snapshot);
      await plants.saveDraft(compact, loc, dateTaken);
      toast.success(t("camera.toastDraftSaved"));
      setSnapshot(null);
      navigate("/gallery");
    } catch (e) {
      const msg = e?.response?.data?.detail || t("camera.toastDraftFail");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const identify = async () => {
    if (!snapshot) return;
    setBusy(true);
    const identifyStartedAt = Date.now();
    setBusyStartedAt(identifyStartedAt);
    let compact = null;
    let loc = null;
    try {
      compact = await compressDataURL(snapshot, 1024);
      loc = includeLocation ? await getLocationOnce() : null;
      const dateTaken = safeExifDateTaken(snapshot);
      const { data } = await identifyWithOneRetry(() => plants.identify(compact, true, loc, dateTaken));
      recordIdentifyDurations([Date.now() - identifyStartedAt]);
      navigate("/result", { state: { scan: data, freshlyIdentified: true } });
    } catch (e) {
      await offerSaveForLaterOnError(e, "camera.toastIdentifyFail", compact, loc);
    } finally { setBusy(false); setBusyStartedAt(null); }
  };

  const identifyMulti = async () => {
    if (multiShots.length < 2) return;
    setBusy(true);
    const identifyStartedAt = Date.now();
    setBusyStartedAt(identifyStartedAt);
    let loc = null;
    try {
      loc = includeLocation ? await getLocationOnce() : null;
      // Prefer the FIRST photo's EXIF — that's typically the wide/main shot
      // the user took at the beginning of the session; subsequent leaf /
      // flower macros usually share the same timestamp anyway.
      const dateTaken = safeExifDateTaken(multiShots[0]);
      const { data } = await identifyWithOneRetry(() => plants.identifyMulti(multiShots, true, loc, dateTaken));
      recordIdentifyDurations([Date.now() - identifyStartedAt]);
      setMultiShots([]);
      setMultiAngle(false);
      navigate("/result", { state: { scan: data, freshlyIdentified: true } });
    } catch (e) {
      // Transient failure after one retry — save the primary shot as a draft
      // so nothing is lost. Extra angles can't attach to a single-photo
      // draft; user can re-add them after the retry succeeds.
      if (isTransientServerFailure(e)) {
        const queued = await queueTransientFailure(multiShots[0], loc);
        if (queued) {
          setMultiShots([]);
          setMultiAngle(false);
          return;
        }
      }
      const msg = e?.response?.data?.detail || t("camera.toastIdentifyFail");
      toast.error(msg);
    } finally { setBusy(false); setBusyStartedAt(null); }
  };

  const removeMultiShot = (i) => setMultiShots((arr) => arr.filter((_, idx) => idx !== i));

  const flipCamera = () => setFacing((f) => (f === "environment" ? "user" : "environment"));

  const onPickFile = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (list.length === 0) return;
    const accepted = list.filter(isSupportedImage);
    const skipped = list.length - accepted.length;
    if (skipped > 0) toast.error(skipped === 1 ? t("camera.toastFiles", { n: skipped }) : t("camera.toastFilesPl", { n: skipped }));
    if (accepted.length === 0) return;

    if (accepted.length === 1) {
      try {
        const raw = await readFileAsDataURL(accepted[0]);
        const compact = await compressDataURL(raw, 1600);
        if (multiAngle) {
          if (multiShots.length >= 4) { toast.error(t("camera.multiMaxed")); return; }
          setMultiShots((arr) => [...arr, compact]);
        } else {
          setSnapshot(compact);
        }
      } catch { toast.error(t("camera.toastReadFail")); }
      return;
    }

    // 2+ files uploaded — in multi-angle mode, fill up to 4 shots; else run bulk identify.
    if (multiAngle) {
      const room = Math.max(0, 4 - multiShots.length);
      if (room === 0) { toast.error(t("camera.multiMaxed")); return; }
      const slice = accepted.slice(0, room);
      try {
        const compacts = await Promise.all(slice.map(async (f) => compressDataURL(await readFileAsDataURL(f), 1600)));
        setMultiShots((arr) => [...arr, ...compacts]);
      } catch { toast.error(t("camera.toastReadFail")); }
      return;
    }

    const nextSlice = bulk.actions.preparePendingBatch(accepted);
    await bulk.actions.start(nextSlice);
  };

  // Bulk view — full-screen "identifying N photos…" experience delegated to
  // BulkQueueView. Everything mutating lives in `bulk.actions`.
  if (bulkQueue.length > 0) {
    return <BulkQueueView bulk={bulk} />;
  }

  return (
    <AppShell>
      <div className="md:max-w-3xl md:mx-auto">
      <div className="px-5 pt-2">
        <h1 className="font-display text-3xl text-leaf-ink leading-tight">
          {snapshot ? t("camera.lookGood") : t("camera.capture")}
        </h1>
        <p className="text-sm text-leaf-mist mt-1">
          {snapshot ? t("camera.previewSub") : t("camera.captureSub")}
        </p>
      </div>

      <div className="mx-5 mt-4 relative rounded-3xl overflow-hidden bg-[#1A231C] aspect-[3/4]" data-testid="camera-frame">
        {error && !snapshot ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-cream text-sm gap-4">
            <span>{error}</span>
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-fallback-button"
              className="rounded-full px-5 py-2.5 bg-white text-leaf-ink font-medium text-sm flex items-center gap-2 hover:bg-parchment transition"
            >
              <ImageIcon className="w-4 h-4" strokeWidth={1.5} /> {t("camera.uploadPhotos")}
            </button>
          </div>
        ) : snapshot ? (
          <img src={snapshot} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="camera-video" aria-label={t("camera.videoAria")} />
        )}
        {!snapshot && !error && <div className="absolute inset-6 border border-cream/30 rounded-2xl pointer-events-none" />}
        {flash && <div className="shutter-flash animate-shutter" />}
      </div>

      <div className="px-5 mt-6">
        {snapshot ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <button onClick={retake} disabled={busy} data-testid="retake-button"
                className="flex-1 rounded-full py-4 bg-parchment text-leaf-ink font-medium flex items-center justify-center gap-2 hover:bg-[#EEE9DE] disabled:opacity-60 active:scale-[0.98] transition">
                <X className="w-4 h-4" strokeWidth={1.5} /> {t("camera.retake")}
              </button>
              <button onClick={identify} disabled={busy} data-testid="identify-button"
                className="flex-[1.4] rounded-full py-4 bg-leaf hover:bg-leaf-deep disabled:opacity-60 text-white font-medium flex items-center justify-center gap-2 active:scale-[0.98] transition shadow-botanical">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                {busy ? singleBusyLabel : t("camera.identify")}
              </button>
            </div>
            {latencyHintSeconds !== null && !busy && (
              <p
                className="text-[11px] text-leaf-mist text-center -mt-1"
                data-testid="identify-latency-hint"
              >
                {t("camera.latencyHint", { seconds: latencyHintSeconds })}
              </p>
            )}
            <button onClick={saveForLater} disabled={busy} data-testid="save-for-later-button"
              className="w-full rounded-full py-3 bg-white border border-[#E8E5DF] text-leaf-ink font-medium flex items-center justify-center gap-2 hover:bg-parchment disabled:opacity-60 active:scale-[0.98] transition">
              <Bookmark className="w-4 h-4" strokeWidth={1.5} />
              {t("camera.saveForLater")}
            </button>
            <p className="text-[11px] text-leaf-mist text-center -mt-1" data-testid="save-for-later-help">
              {t("camera.saveForLaterHelp")}
            </p>
            <button onClick={() => setEditorOpen(true)} disabled={busy} data-testid="edit-photo-button"
              className="w-full rounded-full py-3 bg-white border border-[#E8E5DF] text-leaf-ink font-medium flex items-center justify-center gap-2 hover:bg-parchment disabled:opacity-60 active:scale-[0.98] transition">
              <CropIcon className="w-4 h-4" strokeWidth={1.5} /> {t("camera.editPhoto")}
            </button>
            <label className="flex items-center justify-between gap-3 rounded-2xl bg-white border border-[#E8E5DF] px-4 py-3 cursor-pointer" data-testid="location-toggle-wrap">
              <span className="flex items-start gap-2 text-leaf-ink text-sm">
                <span className="text-leaf mt-0.5">📍</span>
                <span>
                  <span className="font-medium">{t("camera.tagLocation")}</span>
                  <span className="block text-[11px] text-leaf-mist">{t("camera.tagLocationHelp")}</span>
                </span>
              </span>
              <input
                type="checkbox"
                checked={includeLocation}
                onChange={(e) => setIncludeLocation(e.target.checked)}
                data-testid="location-toggle"
                className="w-5 h-5 accent-leaf"
              />
            </label>
          </div>
        ) : (
          <>
            {/* Multi-angle toggle + collected thumbnails */}
            <MultiAngleControls
              multiAngle={multiAngle}
              onToggleMultiAngle={(v) => { setMultiAngle(v); if (!v) setMultiShots([]); }}
              multiShots={multiShots}
              onRemoveShot={removeMultiShot}
              onIdentifyMulti={identifyMulti}
              busy={busy}
              busyLabel={singleBusyLabel}
            />

            <div className="flex items-center justify-center gap-6">
              <button onClick={() => fileInputRef.current?.click()} data-testid="upload-button"
                className="w-12 h-12 rounded-full bg-white border border-[#E8E5DF] text-leaf flex items-center justify-center hover:bg-parchment transition"
                aria-label={t("camera.uploadAria")}>
                <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button onClick={capture} data-testid="capture-button" aria-label={t("camera.captureAria")}
                className="relative w-20 h-20 rounded-full bg-leaf text-white flex items-center justify-center shadow-botanicalLg active:scale-95 transition ring-4 ring-white">
                <CameraIcon className="w-7 h-7" strokeWidth={1.5} />
              </button>
              <button onClick={flipCamera} data-testid="flip-camera-button"
                className="w-12 h-12 rounded-full bg-white border border-[#E8E5DF] text-leaf flex items-center justify-center hover:bg-parchment transition"
                aria-label={t("camera.flipAria")}>
                <RefreshCcw className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </>
        )}
        {!snapshot && (
          <p className="text-center text-[11px] text-leaf-mist mt-4 uppercase tracking-[0.18em]">
            {t("camera.hint", { n: MAX_BULK })}
          </p>
        )}
      </div>

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
        onChange={onPickFile} className="hidden" data-testid="upload-input" />

      <PhotoEditor
        open={editorOpen && !!snapshot}
        image={snapshot}
        onCancel={() => setEditorOpen(false)}
        onConfirm={(edited) => { setSnapshot(edited); setEditorOpen(false); }}
      />
      </div>
    </AppShell>
  );
}
